/**
 * Console globale de l'enrichissement (page Enrichissement) : ce que les
 * derniers passages ont donné, source par source — résultats, rien trouvé,
 * refus d'accès, erreurs — et la liste des passages, les problèmes en tête.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/crmApi";
import { RunCard } from "@/crm/components/EnrichmentConsole";

export default function EnrichmentRunsConsole({ refreshKey = 0, canPurge = false }) {
  const [data, setData] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/enrichment/console", { params: { limit: 40, only_problems: onlyProblems } })
      .then(({ data: d }) => setData(d)).catch(() => setData({ runs: [], by_provider: {}, total_runs: 0, problem_runs: 0 }))
      .finally(() => setLoading(false));
  }, [onlyProblems]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const purge = async () => {
    if (!window.confirm("Vider la console des enrichissements ? Les preuves et la provenance des fiches sont conservées.")) return;
    try { await api.delete("/enrichment/console"); toast.success("Console vidée"); load(); }
    catch { toast.error("Purge impossible"); }
  };

  const sources = Object.values(data?.by_provider || {});
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="enrichment-runs-console">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="font-heading font-semibold text-slate-800">Console des enrichissements</h3>
        {data && (
          <span className="text-xs text-slate-500" data-testid="console-totals">
            {data.total_runs} passage{data.total_runs > 1 ? "s" : ""} · <b className={data.problem_runs ? "text-rose-600" : ""}>{data.problem_runs}</b> avec problème
          </span>
        )}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} data-testid="console-only-problems" />
          <ShieldAlert size={12} className="text-rose-500" /> Problèmes seulement
        </label>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Actualiser</button>
        {canPurge && (
          <button onClick={purge} data-testid="console-purge" title="Vider la console"
            className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"><Trash2 size={12} /> Purger</button>
        )}
      </div>

      {sources.length > 0 && (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="console-by-source">
            <thead className="text-left text-[11px] uppercase tracking-wide text-slate-400">
              <tr><th className="py-1 pr-2">Source</th><th className="py-1 pr-2 text-right">Passages</th><th className="py-1 pr-2 text-right text-emerald-700">Résultats</th>
                <th className="py-1 pr-2 text-right">Rien</th><th className="py-1 pr-2 text-right text-rose-600">Refus</th><th className="py-1 pr-2 text-right text-rose-600">Erreurs</th><th className="py-1 text-right text-slate-400">Ignorée</th></tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.provider} className="border-t border-slate-50" data-testid={`console-source-${s.provider}`}>
                  <td className="py-1 pr-2 text-slate-700">{s.label}</td>
                  <td className="py-1 pr-2 text-right font-mono text-slate-500">{s.runs}</td>
                  <td className="py-1 pr-2 text-right font-mono text-emerald-700">{s.found}</td>
                  <td className="py-1 pr-2 text-right font-mono text-slate-500">{s.nothing}</td>
                  <td className={`py-1 pr-2 text-right font-mono ${s.blocked ? "font-semibold text-rose-600" : "text-slate-300"}`}>{s.blocked}</td>
                  <td className={`py-1 pr-2 text-right font-mono ${s.error ? "font-semibold text-rose-600" : "text-slate-300"}`}>{s.error}</td>
                  <td className="py-1 text-right font-mono text-slate-300">{s.skipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
        {!data ? <p className="text-sm text-slate-400">Chargement…</p>
          : data.runs.length === 0 ? (
            <p className="text-sm text-slate-400">{onlyProblems ? "Aucun passage à problème." : "Aucun enrichissement tracé pour l'instant : lancez-en un, la console se remplit passage par passage."}</p>
          ) : data.runs.map((run) => <RunCard key={run.id} run={run} defaultOpen={Boolean(run.has_problem)} showCompany />)}
      </div>
    </div>
  );
}
